import React from "react";
// TRANSLATION
import { useTranslation } from "react-i18next";
// ANT DESIGN COMPONENTS
import { Select, InputNumber, Button, Tooltip, Alert } from "antd";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
// COMPONENTS
import FormInputText from "../../../../components/form/FormInputText";
import OpcUaFieldLabel from "./OpcUaFieldLabel";
import OpcUaTextField from "./OpcUaTextField";
// INTERFACES
import type {
  OpcUaNodeIdForm,
  OpcUaNodeIdentifierKind,
  OpcUaRelativePathHopForm,
} from "../../../../../interfaces/AdapterCdoOpcUa.interface";

const { Option } = Select;

export const BLANK_NUMERIC_NODE_ID: OpcUaNodeIdForm = {
  namespaceIndex: 2,
  identifierKind: "string",
  identifier: "",
};

const BLANK_HOP: OpcUaRelativePathHopForm = {
  referenceTypeNode: null,
  isInverse: false,
  includeSubtypes: true,
  targetNamespaceIndex: 2,
  targetName: "",
};

/** Placeholder text per identifier kind — clarifies the compound encodings. */
const IDENTIFIER_PLACEHOLDER: Partial<Record<OpcUaNodeIdentifierKind, string>> = {
  bytestring: "1,2,255",
};

function identifierLabel(kind: OpcUaNodeIdentifierKind): string {
  return IDENTIFIER_PLACEHOLDER[kind]
    ? `Node Identifier (${IDENTIFIER_PLACEHOLDER[kind]})`
    : "Node Identifier";
}

/** One ordered hop editor row for BrowsePath addressing. */
function HopRow({
  hop,
  onChangeHop,
  onRemove,
  disabled = false,
}: {
  hop: OpcUaRelativePathHopForm;
  onChangeHop: (hop: OpcUaRelativePathHopForm) => void;
  onRemove: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="row mb-2"
      style={{ backgroundColor: "#1f1f1f", borderRadius: "5px", padding: "8px" }}
    >
      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">Target Namespace</span>
        <input
          type="number"
          className="generalStyles__input generalStyles__width100"
          value={hop.targetNamespaceIndex}
          disabled={disabled}
          onChange={(e) =>
            onChangeHop({ ...hop, targetNamespaceIndex: Number(e.target.value) || 0 })
          }
        />
      </div>
      <div className="col-12 col-md-5 mt-2">
        <FormInputText
          inputValue={hop.targetName}
          inputLabelName="Target Name"
          inputOnChange={(value) => onChangeHop({ ...hop, targetName: value })}
          inputVariableName="targetName"
          isRequired
          disabledInput={disabled}
        />
      </div>
      <div className="col-6 col-md-2 mt-2">
        <div className="generalStyles__flex">
          <span className="generalStyles__inputLabel">Inverse</span>
          <input
            type="checkbox"
            checked={hop.isInverse}
            disabled={disabled}
            onChange={(e) => onChangeHop({ ...hop, isInverse: e.target.checked })}
            style={{ marginLeft: 8 }}
          />
        </div>
      </div>
      <div className="col-4 col-md-1 mt-2 generalStyles__flexCenter">
        <Tooltip title="Remove hop">
          <Button
            onClick={onRemove}
            disabled={disabled}
            className="buttonStyle__29"
            shape="circle"
            icon={<FontAwesomeIcon icon={faTrash} />}
          />
        </Tooltip>
      </div>
    </div>
  );
}

/**
 * Shared NodeId sub-form covering all 5 NodeIdentifier kinds
 * (numeric/string/guid/bytestring/browsepath). BrowsePath nests a starting
 * node (recursed one level via a compact numeric/string picker only, to
 * avoid unbounded recursion) plus an ordered hop list.
 */
export default function NodeIdEditor({
  node,
  onChangeNode,
  label = "Node",
  disabled = false,
}: {
  node: OpcUaNodeIdForm;
  onChangeNode: (node: OpcUaNodeIdForm) => void;
  label?: string;
  disabled?: boolean;
}) {
  const [t] = useTranslation("global");

  const onAddHop = () => {
    onChangeNode({ ...node, hops: [...(node.hops ?? []), { ...BLANK_HOP }] });
  };
  const onChangeHopAt = (index: number, hop: OpcUaRelativePathHopForm) => {
    const hops = [...(node.hops ?? [])];
    hops[index] = hop;
    onChangeNode({ ...node, hops });
  };
  const onRemoveHopAt = (index: number) => {
    onChangeNode({ ...node, hops: (node.hops ?? []).filter((_, i) => i !== index) });
  };

  const startingNode = node.startingNode ?? BLANK_NUMERIC_NODE_ID;
  const onChangeStartingNode = (updated: OpcUaNodeIdForm) =>
    onChangeNode({ ...node, startingNode: updated });

  return (
    <div className="row">
      {node.identifierKind !== "browsepath" && (
        <div className="col-12 col-md-4 mt-2">
          <OpcUaFieldLabel
            label={`${label} Namespace Index`}
            tooltip={t("general.opcUaNodeNamespaceIndexHelp")}
          />
          <InputNumber
            className="generalStyles__width100"
            size="large"
            style={{ width: "100%", marginTop: 8 }}
            min={0}
            value={node.namespaceIndex}
            disabled={disabled}
            onChange={(v) => onChangeNode({ ...node, namespaceIndex: v ?? 0 })}
          />
        </div>
      )}
      <div className="col-12 col-md-4 mt-2">
        <OpcUaFieldLabel
          label={`${label} Identifier Kind`}
          tooltip={t("general.opcUaNodeIdentifierKindHelp")}
        />
        <Select
          className="generalStyles__width100"
          size="large"
          style={{ width: "100%", marginTop: 8 }}
          value={node.identifierKind}
          disabled={disabled}
          onChange={(kind: OpcUaNodeIdentifierKind) =>
            onChangeNode({ ...node, identifierKind: kind })
          }
        >
          <Option value="numeric">Numeric</Option>
          <Option value="string">String</Option>
          <Option value="guid">Guid</Option>
          <Option value="bytestring">ByteString</Option>
          {/*
            BrowsePath is NOT offered for new nodes: opc_ua_protocol's codec
            rejects it outright (`UnsupportedNodeAddressing`), and the bank
            node->lift binding cannot key on it either, so an adapter authored
            with one cannot start. The option stays available only when a node
            ALREADY uses it, so existing config remains visible and editable
            rather than silently reverting to another kind.
          */}
          {node.identifierKind === "browsepath" && (
            <Option value="browsepath">BrowsePath (unsupported)</Option>
          )}
        </Select>
      </div>
      {node.identifierKind !== "browsepath" && (
        <div className="col-12 col-md-4 mt-2">
          <OpcUaTextField
            label={identifierLabel(node.identifierKind)}
            value={node.identifier}
            onChange={(value) => onChangeNode({ ...node, identifier: value })}
            required
            disabled={disabled}
            tooltip={t("general.opcUaNodeIdentifierHelp")}
          />
        </div>
      )}
      {node.identifierKind === "browsepath" && (
        <div className="col-12 mt-3">
          <Alert
            type="warning"
            showIcon
            className="mb-2"
            message="Browse-path addressing is not supported at runtime"
            description="The vendor translation server rejects browse-path nodes when it builds the connection. Switch this node to Numeric, String, Guid or ByteString before saving."
          />
          <span className="generalStyles__inputLabel">Starting Node</span>
          <div
            className="container mt-2"
            style={{ backgroundColor: "#161616", borderRadius: "5px" }}
          >
            <NodeIdEditor
              node={startingNode}
              onChangeNode={onChangeStartingNode}
              label="Start"
              disabled={disabled}
            />
          </div>
          <div className="mt-3 generalStyles__flex" style={{ justifyContent: "space-between" }}>
            <span className="generalStyles__inputLabel">Hops ({(node.hops ?? []).length})</span>
            <Button onClick={onAddHop} disabled={disabled} className="buttonStyle__28">
              <FontAwesomeIcon icon={faPlus} className="generalStyles__mrFix" />
              Add Hop
            </Button>
          </div>
          {(node.hops ?? []).map((hop, index) => (
            <HopRow
              key={`${index}-${hop.targetNamespaceIndex}-${hop.targetName}`}
              hop={hop}
              onChangeHop={(updated) => onChangeHopAt(index, updated)}
              onRemove={() => onRemoveHopAt(index)}
              disabled={disabled}
            />
          ))}
        </div>
      )}
    </div>
  );
}
